import React, { useEffect, useState } from "react";

import { cn } from "@perceptron/utils/cn";

/** How long a settling shimmer takes to fade into its settled tone; the CSS transition in theme.css matches it. */
export const SHIMMER_SETTLE_MS = 500;

type ShimmerPhase = "streaming" | "settling" | "settled";

/**
 * Text that shimmers while a call runs and, when it stops, winds down rather than cutting off: the
 * highlight keeps sweeping while it fades into the settled tone, and only then does the text take its
 * plain settled colour. Text that is already settled when it mounts never shimmers.
 */
export function ShimmerText({
  as: Tag = "span",
  isStreaming,
  settledTone = "primary",
  highlightOnHover = false,
  className,
  children,
  ...props
}: {
  as?: "span" | "p";
  isStreaming: boolean;
  /** The colour it rests in once settled: primary (white) or secondary. */
  settledTone?: "primary" | "secondary";
  /** Once settled, brighten to primary while a `group` ancestor (the link) is hovered or focused. */
  highlightOnHover?: boolean;
  className?: string;
  children: React.ReactNode;
} & Omit<React.HTMLAttributes<HTMLElement>, "className" | "children">) {
  const [phase, setPhase] = useState<ShimmerPhase>(isStreaming ? "streaming" : "settled");
  useEffect(() => {
    if (isStreaming) {
      setPhase("streaming");
      return;
    }
    setPhase((current) => (current === "settled" ? "settled" : "settling"));
    const timer = setTimeout(() => setPhase("settled"), SHIMMER_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [isStreaming]);

  return (
    <Tag
      className={cn(
        className,
        // The settled colour is taken as soon as settling starts: the shimmer's transparent fill hides it
        // until then, so settling never ends in a colour change for the hover transition to animate.
        phase !== "streaming" && settledTone === "secondary" ? "text-foreground-secondary" : "text-foreground-primary",
        {
          "tool-call-text-streaming": phase !== "settled",
          [`tool-call-text-settling-${settledTone}`]: phase === "settling",
          // Only when settled, so the shimmer's own transition is never replaced.
          "transition-colors duration-200 ease-out group-hover:text-foreground-primary group-focus-visible:text-foreground-primary":
            highlightOnHover && phase === "settled",
        },
      )}
      {...props}
    >
      {children}
    </Tag>
  );
}
